import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { VehicleRestitutionReturnStep } from "./vehicleRestitutionReturnStep";
import { VehicleRestitutionConditionStep } from "./vehicleRestitutionConditionStep";
import { VehicleRestitutionSummaryStep } from "./vehicleRestitutionSummaryStep";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectVehicleUuid } from "../../qrCode/vehicleLoginSlice";
import {
  createInitialVehicleRestitutionAsync,
  selectCreateInitialVehicleRestitutionStatus,
  vehicleSessionsSlice,
} from "./vehicleSessionsSlice";
import { ErrorPage } from "../../components/ErrorPage";

type RestitutionStep = "return" | "condition" | "summary";

export const VehicleRestitutionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const vehicleUuid = useAppSelector(selectVehicleUuid);
  const createInitialVehicleRestitutionStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );
  const [step, setStep] = useState<RestitutionStep>("return");
  const [departureLocation, setDepartureLocation] = useState("");
  const [furthestDestination, setFurthestDestination] = useState("");
  const [mileage, setMileage] = useState("");
  const [isCabinCleaned, setIsCabinCleaned] = useState(false);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(false);
  const [observations, setObservations] = useState("");

  useEffect(() => {
    if (createInitialVehicleRestitutionStatus === "failed") {
      triggerToastDS(t("restitution.summary.error.text") || "", "ERROR");
      dispatch(vehicleSessionsSlice.actions.resetStatus());
      navigate("/menu");
    }
  }, [createInitialVehicleRestitutionStatus, t, dispatch, navigate]);

  if (!vehicleUuid) return <ErrorPage page="qrCode" />;

  const submitRestitution = () => {
    void dispatch(
      createInitialVehicleRestitutionAsync({
        vehicleUuid,
        mileage: Number(mileage),
        isCabinCleaned,
        isTrunkCleaned,
        observations: observations === "" ? null : observations,
        departureLocation,
        furthestDestination,
      }),
    );
    setStep("summary");
  };

  switch (step) {
    case "return":
      return (
        <VehicleRestitutionReturnStep
          departureLocation={departureLocation}
          setDepartureLocation={setDepartureLocation}
          furthestDestination={furthestDestination}
          setFurthestDestination={setFurthestDestination}
          mileage={mileage}
          setMileage={setMileage}
          onReturn={() => navigate(-1)}
          onSubmit={() => setStep("condition")}
        />
      );
    case "condition":
      return (
        <VehicleRestitutionConditionStep
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          onReturn={() => setStep("return")}
          onValidate={submitRestitution}
        />
      );
    case "summary":
      if (createInitialVehicleRestitutionStatus === "processing") {
        return <DriverLoadingPage />;
      }
      return (
        <VehicleRestitutionSummaryStep
          departureLocation={departureLocation}
          furthestDestination={furthestDestination}
          mileage={mileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
        />
      );
  }
};
